import { match, P } from 'ts-pattern';
import { zfd } from 'zod-form-data';
import { crewOrderContext } from '~/app/features/order-middleware/orderMiddleware';
import { settingsContext } from '~/app/settingsMiddleware';
import api from '~/utils/api';
import { makeAction, makeLoader, redirect } from '~/utils/routing';
import { orderEditActionSchema } from '~/utils/schemas';

export const actionFormKey = 'order-edit-action';

const schema = zfd.formData({
  [actionFormKey]: zfd.json(orderEditActionSchema),
});

export const { action, useActionResult } = makeAction(
  async ({ context, formData }, appStore) => {
    const settings = context.get(settingsContext);
    const order = context.get(crewOrderContext);

    return match(schema.safeParse(formData))
      .with({ success: false }, () => redirect('../'))
      .with({ success: true, data: P.select() }, async (data) => {
        const orderEditAction = data[actionFormKey];
        const prepared = await match(orderEditAction)
          .with({ editType: 'UPDATE_SHIPPING_LINE' }, async (a) =>
            api.prepareOrderEdit({
              params: {
                storefrontId: settings.storefrontId,
                storeOrderId: order.id,
              },
              body: { editType: a.editType, action: a },
            }),
          )
          .otherwise(() => undefined);

        if (
          prepared?.editType === 'UPDATE_SHIPPING_LINE' &&
          prepared.prepared.quotes.length === 0
        ) {
          return {
            ok: false,
            message:
              'No additional shipping methods are currently available for this order. Please contact support for assistance.',
          };
        }

        appStore.dispatch(
          appStore.actions.orderEdit.assignAction(orderEditAction),
        );
        if (prepared) {
          appStore.dispatch(
            appStore.actions.orderEdit.assignPrepared(prepared),
          );
        }

        return redirect(`../edit/stage`);
      })
      .exhaustive();
  },
);

export const { loader, useLoaderData } = makeLoader(({ context }) => {
  const settings = context.get(settingsContext);
  const order = context.get(crewOrderContext);

  return api
    .startOrderEdit({
      params: {
        storefrontId: settings.storefrontId,
        storeOrderId: order.id,
      },
    })
    .then((data) => ({
      orderEditActions: data,
      orderEditExpiredDescriptionText:
        settings.theme.orderEditExpiredDescriptionText,
    }));
});
